import { initializeFaro, getWebInstrumentations, faro, LogLevel } from "@grafana/faro-web-sdk";
import { ORGANIZATION_ID } from "../const";

/**
 * Fehler, die aus einer Browser-Erweiterung stammen (Stack-Frames unter
 * chrome-extension://, moz-extension://, safari-web-extension://), sind kein
 * App-Fehler und nicht actionable. Beispiel 21.09.2026: Urban VPN Proxy warf
 * 56× "Cannot read properties of undefined (reading 'M_ID')" in einer Session.
 */
export function isBrowserExtensionItem(item: {type: string; payload: unknown}): boolean {
    if (item.type !== "exception" && item.type !== "log") {
        return false;
    }
    // ponytail: Stringify statt Frame-Traversal — Faro legt den Stack je nach Typ
    // in payload.stacktrace.frames[].filename (exception) oder payload.context/message (log) ab.
    return /(chrome|moz|safari-web)-extension:\/\//.test(JSON.stringify(item.payload));
}

// Bedienelemente, deren Klick wir für den Klickweg im Support aufzeichnen.
// Bewusst ohne Tabs, Checkbox-/Radio-Labels und Select-Optionen: deren Text sind oft
// Kundennamen, ohne Text wäre das Event aber nichtssagend.
const CLICK_TARGET = "button, a, [role=button], [role=menuitem]";

// Beschriftungen, die als Klartext nach Faro dürfen. Alles andere kann Kundendaten tragen
// (`Zu ${fullName} übertragen`, `Per SMS senden an ${mobileNumber}`) und wird nicht gesendet.
// ponytail: feste Wortliste statt Abgleich mit den Übersetzungen; fehlt ein Wort, Liste ergänzen.
const SAFE_LABELS = (
    "ok ja nein speichern abbrechen schließen löschen entfernen bearbeiten hinzufügen weiter zurück übernehmen " +
    "anwenden bestätigen senden erstellen suchen zurücksetzen exportieren drucken herunterladen hochladen " +
    "stornieren kopieren aktualisieren neu fertig " +
    // Englische Oberfläche
    "yes no save cancel close delete remove edit add next back apply confirm send create search reset export " +
    "print download upload copy refresh new done"
).split(" ");

/**
 * Beschreibt das angeklickte Bedienelement für ein Faro-Event, `null` bei
 * Klicks ins Leere. Erfasst nie Eingabewerte und keine frei formulierten Texte:
 * Beschriftung nur aus der Wortliste oder dem antd-Icon-Namen, bei Links nur den Pfad.
 */
export function describeClickTarget(target: EventTarget | null): {element: string; label: string} | null {
    if (!(target instanceof Element) || target.tagName === "INPUT") {
        // INPUT: antd-Upload leitet den Button-Klick als zweiten Klick an ein verstecktes Input weiter
        return null;
    }
    const el = target.closest(CLICK_TARGET);
    if (!el) {
        // antd-Icons mit eigenem onClick ohne Button drumherum (z. B. Stift an Rabatt/Guthaben)
        const icon = target.closest(".anticon");
        return icon ? {element: "icon", label: icon.getAttribute("aria-label") || ""} : null;
    }
    if (el.tagName === "A") {
        return {element: "a", label: describeHref(el.getAttribute("href"))};
    }
    const text = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim();
    const iconLabel = el.querySelector(".anticon[aria-label]");
    const label = SAFE_LABELS.indexOf(text.toLowerCase()) !== -1 ? text : iconLabel ? iconLabel.getAttribute("aria-label") || "" : "";
    return {element: el.getAttribute("role") || el.tagName.toLowerCase(), label: label};
}

/**
 * Eigene Routen: nur der Pfad (enthält nur IDs). Fremde http(s)-Links: nur der Host, weil
 * Pfade wie wa.me/<Nummer> Kontaktdaten tragen. Alle anderen Schemata (mailto, tel, sms,
 * in jeder Schreibweise): nur das Schema.
 */
function describeHref(href: string | null): string {
    if (!href) {
        return "";
    }
    try {
        const url = new URL(href, window.location.href);
        if (url.origin === window.location.origin) {
            return url.pathname;
        }
        return /^https?:$/.test(url.protocol) ? url.host : url.protocol.slice(0, -1);
    } catch (e) {
        return "";
    }
}

function trackClick(event: MouseEvent): void {
    const description = describeClickTarget(event.target);
    if (description && faro.api) {
        faro.api.pushEvent("click", description);
    }
}

export function initFaro(): void {
    const collectorUrl = process.env.FARO_COLLECTOR_URL;
    const enabled = process.env.FARO_ENABLED === "true";

    if (!collectorUrl || !enabled) {
        return;
    }

    initializeFaro({
        url: collectorUrl,
        app: {
            name: "ko-admin",
            version: process.env.APP_VERSION || "unknown",
            environment: process.env.DEPLOY_ENV || "development",
        },
        sessionTracking: {
            enabled: true,
        },
        // console.error wird als Log (level=error) statt als Exception erfasst.
        // Dadurch landen die zahlreichen antd-/React-Deprecation-Warnungen
        // ("console.error: Warning: ...") im "Console Errors"-Panel und verrauschen
        // weder den Exception-Stream noch die Exception-Spike-Alerts. Echte
        // unbehandelte JS-Fehler kommen aus der Errors-Instrumentation und bleiben
        // weiterhin kind=exception.
        //
        // Hinweis: disabledLevels MUSS hier stehen — die frühere Option
        // captureConsoleDisabledLevels an getWebInstrumentations() war wirkungslos,
        // weil getWebInstrumentations die ConsoleInstrumentation ohne Optionen baut.
        consoleInstrumentation: {
            consoleErrorAsLog: true,
            disabledLevels: [LogLevel.LOG, LogLevel.DEBUG, LogLevel.INFO, LogLevel.TRACE],
        },
        instrumentations: [
            ...getWebInstrumentations({
                captureConsole: true,
            }),
        ],
        beforeSend: (item) => {
            // Harmloses Rauschen herausfiltern (Filter-Parität mit Sentry)
            if (isBrowserExtensionItem(item)) {
                return null;
            }
            if (item.type === "exception" || item.type === "log") {
                // Faro v2: Exceptions nutzen payload.values[] Array, Logs nutzen payload.message
                var payload = item.payload as {value?: string; message?: string; values?: Array<{value?: string}>};
                var message = payload.message || payload.value || "";
                if (!message && payload.values && payload.values.length > 0) {
                    message = payload.values[0].value || "";
                }

                // ResizeObserver-Fehler (Ant Design Rauschen)
                if (message.indexOf("ResizeObserver") !== -1) {
                    return null;
                }

                // Cross-Origin Script-Fehler ohne Stack Trace (3rd Party, nicht actionable)
                if (message === "Script error.") {
                    return null;
                }

                // React-Dev-Warnings: nicht actionable, verrauschen das Dashboard
                if (
                    message.indexOf("Each child in a list should have a unique") !== -1 ||
                    message.indexOf("React does not recognize the") !== -1 ||
                    message.indexOf("Can't perform a React state update on an unmounted") !== -1 ||
                    message.indexOf("deprecated with perf issue") !== -1
                ) {
                    return null;
                }

                // Maintenance-/Wartungsfehler (werden separat im UI behandelt)
                var lower = message.toLowerCase();
                if (
                    lower.indexOf("service unavailable") !== -1 ||
                    lower.indexOf("maintenance") !== -1 ||
                    lower.indexOf("wartung") !== -1
                ) {
                    return null;
                }
            }

            if (item.meta) {
                item.meta.app = {
                    ...item.meta.app,
                };
            }
            return item;
        },
        metas: [
            () => ({
                app: {
                    name: "ko-admin",
                    version: process.env.APP_VERSION || "unknown",
                    environment: process.env.DEPLOY_ENV || "development",
                    namespace: ORGANIZATION_ID || "unknown",
                },
                page: {
                    url: window.location.href,
                },
            }),
        ],
    });

    // Faros eigene Klick-Erfassung (UserActionInstrumentation) greift nur bei
    // Elementen mit data-faro-user-action-name, das tragen unsere Buttons nicht.
    // Capture-Phase, damit stopPropagation in antd-Komponenten den Klick nicht verschluckt.
    document.addEventListener("click", trackClick, true);
}

/**
 * User-Kontext in Faro setzen bei Login.
 * @param userId - FusionAuth User-ID
 * @param email - E-Mail-Adresse des Users
 * @param firstName - Vorname des Users
 * @param lastName - Nachname des Users
 */
export function setFaroUser(
    userId: string,
    email?: string,
    firstName?: string,
    lastName?: string,
): void {
    try {
        if (faro.api) {
            const fullName = [firstName, lastName].filter(Boolean).join(" ");
            faro.api.setUser({
                id: userId,
                email: email || undefined,
                username: firstName || undefined,
                attributes: fullName ? { fullName: fullName } : undefined,
            });
        }
    } catch (e) {
        // Faro nicht initialisiert oder deaktiviert - kein Problem
    }
}

/**
 * User-Kontext in Faro loeschen bei Logout.
 */
export function clearFaroUser(): void {
    try {
        if (faro.api) {
            faro.api.resetUser();
        }
    } catch (e) {
        // Faro nicht initialisiert oder deaktiviert - kein Problem
    }
}

/**
 * Aktuelle Faro-Session-ID, über die sich in Loki alle Frontend-Ereignisse
 * dieser Browser-Sitzung finden. `undefined`, wenn Faro aus ist.
 */
export function getFaroSessionId(): string | undefined {
    try {
        return faro.api?.getSession()?.id;
    } catch (e) {
        return undefined;
    }
}
